/**
 * VERSION A — "DEEP FIELD"
 *
 * The question this version asks: **can the frame have real DEPTH instead of a background?**
 *
 * Every plate is a photoreal still cut into depth slices and rebuilt as a CSS-3D stage, so the
 * camera physically dollies through the space — near layers grow faster than far ones, which a
 * Ken Burns zoom can never fake. Fog planes sit BETWEEN the slices. Type is placed at a depth,
 * not pasted on the front.
 *
 * ⛔ There is no panorama, no sandalwood ground, no flat illustration anywhere in this version.
 * ⛔ Nothing is ever empty: if a plate has sky in it, the sky carries haze, grain and a ray pass.
 *
 * Register: cold, cinematic, near-monochrome, ONE ember accent.
 * Type: Matangi 900 (headline) / Khand 700 (labels).
 */
import React from "react";
import { AbsoluteFill, Audio, AbsoluteFill as Fill, staticFile, Easing } from "remotion";
import { loadFont as Matangi } from "@remotion/google-fonts/Matangi";
import { loadFont as Khand } from "@remotion/google-fonts/Khand";
import {
  Depth3D,
  Fog,
  GodRays,
  Grain,
  Vignette,
  Plate,
  Punch,
  Label,
  Highlight,
  DrawLine,
  Scribble,
  useKeys,
  useSpringAt,
  useStep,
  devaBlock,

} from "./kit45";
import { A } from "./layersA";

const head = Matangi("normal", { weights: ["900", "700"], subsets: ["devanagari"] }).fontFamily;
const lab = Khand("normal", { weights: ["600", "700"], subsets: ["devanagari", "latin"] }).fontFamily;

const P = 3400;
const EMBER = "#F0913A";
const sf = (p: string) => staticFile(p);
const lay = (k: keyof typeof A, tune: Partial<Record<number, { blur?: number; haze?: number }>> = {}) =>
  A[k].map((l, i) => ({ ...l, src: sf(l.src), blur: tune[i]?.blur ?? l.blur, haze: tune[i]?.haze ?? l.haze }));

/* ── P1 · 0.0-7.0 — THE SIX OCCASIONS ──────────────────────────────────────
   Yash's GRID→STRIP device: each occasion arrives LARGE on its own spoken word, then all six
   travel down into a bottom rail together. One object each, moving — never a big copy swapped
   for a small one. This is what stops the frame being empty while six things are named. */
const OCC: [number, string][] = [
  [0.30, "जन्म"],
  [1.58, "मुंडन"],
  [2.40, "शादी"],
  [3.28, "गृह प्रवेश"],
  [4.26, "हवन"],
  [5.94, "अंतिम संस्कार"],
];
const RAIL_AT = 6.05;

const Occasion: React.FC<{ at: number; text: string; i: number }> = ({ at, text, i }) => {
  const s = useSpringAt(at, { damping: 13, stiffness: 175, mass: 0.8 });
  // big in its OWN grid cell → then all six travel together into the bottom rail.
  // ⛔ they may NOT share a centre point: six words stacked on one spot is unreadable mush.
  const trav = useKeys([[RAIL_AT, 0], [RAIL_AT + 0.85, 1]], Easing.inOut(Easing.cubic));
  const col = i % 3;
  const row = Math.floor(i / 3);
  const gridX = (col - 1) * 372;
  const gridY = (row - 0.5) * 168;
  const slotX = (i - 2.5) * 202;
  const x = gridX + (slotX - gridX) * trav;
  const y = gridY + (268 - gridY) * trav;
  const scale = (0.72 + s * 0.28) * (1 - trav * 0.52);
  const dim = 1 - trav * 0.36;
  return (
    <div
      data-audit="occ"
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: `translate(-50%,-50%) translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${scale.toFixed(3)})`,
        opacity: s,
      }}
    >
      <span
        style={{
          ...devaBlock,
          fontFamily: head,
          fontWeight: 900,
          fontSize: 64,
          color: `rgba(246,240,230,${dim.toFixed(2)})`,
          textShadow: "0 6px 34px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)",
          whiteSpace: "pre",
        }}
      >
        {text}
      </span>
    </div>
  );
};

/* ── P5 · a word carved at its own depth on its own pillar ─────────────────── */
const Carved: React.FC<{ at: number; text: string; z: number; x: number; y: number }> = ({
  at,
  text,
  z,
  x,
  y,
}) => {
  const s = useSpringAt(at, { damping: 15, stiffness: 160 });
  const sc = (P + z) / P;
  return (
    <div
      style={{
        position: "absolute",
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-50%,-50%) translateZ(${-z}px) scale(${(sc * (0.9 + s * 0.1)).toFixed(3)})`,
        transformStyle: "preserve-3d",
        opacity: s,
      }}
    >
      <span
        style={{
          ...devaBlock,
          fontFamily: head,
          fontWeight: 900,
          fontSize: 62,
          color: "rgba(238,232,222,0.94)",
          textShadow: "0 2px 0 rgba(0,0,0,0.55), 0 -1px 0 rgba(255,255,255,0.16), 0 10px 30px rgba(0,0,0,0.8)",
          whiteSpace: "pre",
        }}
      >
        {text}
      </span>
    </div>
  );
};

/* ── the four promised questions, as labels in the four doorways ───────────── */
const DOORS: [number, string, number][] = [
  [22.75, "ये क्या हैं", 20],
  [23.66, "महत्व", 39],
  [25.44, "शादी में क्या", 58],
  [27.72, "विज्ञान", 77],
];

/* ── P8 · the ancestor climb ────────────────────────────────────────────────
   Each spoken "पिता" lights one figure further back. The clip ends mid-climb — an open loop. */
const CLIMB: [number, number][] = [
  [43.81, 0],
  [45.33, 1],
];

export const VerA_DeepField: React.FC = () => {
  return (
    <AbsoluteFill style={{ backgroundColor: "#05070B" }}>
      <Audio src={sf("sanatan/audio/gotra45.mp3")} />

      {/* ── P1 0.0-7.0 · THE SIX OCCASIONS ─────────────────────────────────── */}
      <Plate from={0} to={7.0} lead={0}>
        <Depth3D
          P={P}
          pad={1.16}
          layers={lay("A1_courtyard")}
          cam={[
            [0, 0, 0, 0],
            [7.0, -26, 6, 118],
          ]}
        >
          <Fog z={900} opacity={0.34} color="120,142,170" P={P} />
        </Depth3D>
        <GodRays x="66%" y="-10%" angle={13} opacity={0.24} />
        <Fill style={{ background: "linear-gradient(180deg, rgba(4,6,10,0.55) 0%, rgba(4,6,10,0.08) 38%, rgba(4,6,10,0.72) 100%)" }} />
        {OCC.map(([t, w], i) => (
          <Occasion key={w} at={t} text={w} i={i} />
        ))}
        {/* the rail these six land on — it draws itself just before they arrive */}
        <DrawLine at={RAIL_AT - 0.2} d="M 120 606 L 1160 606" len={1040} color="rgba(240,145,58,0.5)" strokeWidth={2} dur={0.5} />
        <Label at={6.5} text="हर बार" size={22} color="rgba(240,145,58,0.9)" style={{ position: "absolute", left: 120, top: 622, fontFamily: lab }} />
      </Plate>

      {/* ── P2 7.0-12.2 · THE PANDIT / THE QUESTION ────────────────────────── */}
      <Plate from={7.0} to={12.2}>
        <Depth3D
          P={P}
          pad={1.16}
          layers={lay("A2_register")}
          cam={[
            [7.0, 30, -10, 0],
            [10.4, 0, 0, 96],
            [12.2, -6, 2, 132],
          ]}
        >
          <Fog z={700} opacity={0.22} color="90,70,44" P={P} />
        </Depth3D>
        <Vignette strength={0.72} />
        <Label at={7.6} text="वही सवाल, हर बार" size={24} color="rgba(238,228,210,0.66)"
          style={{ position: "absolute", left: 92, top: 92, fontFamily: lab }} />
        {/* THE punch card of the whole 45s — it owns the frame */}
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "relative", textAlign: "center" }}>
            <Punch at={10.69} text="आपका गोत्र" size={128} color="#F6F0E6" weight={900}
              style={{ fontFamily: head, textShadow: "0 10px 50px rgba(0,0,0,0.9)" }} />
            <div style={{ height: 6 }} />
            <Punch at={11.49} text="क्या है" size={128} color={EMBER} weight={900}
              style={{ fontFamily: head, textShadow: "0 10px 50px rgba(0,0,0,0.9)" }} />
          </div>
        </div>
      </Plate>

      {/* ── P3 12.2-15.9 · THE PHONE CALL ──────────────────────────────────── */}
      <Plate from={12.2} to={15.9}>
        <Depth3D
          P={P}
          pad={1.16}
          layers={lay("A3_phonecall")}
          cam={[
            [12.2, -40, 12, 0],
            [15.9, 8, -6, 104],
          ]}
        />
        <Vignette strength={0.66} color="4,10,18" />
        {/* the joke is the label, not a sentence */}
        <div style={{ position: "absolute", left: "50%", top: "27%", transform: "translateX(-50%)" }}>
          <Punch at={14.79} text="घर पर फ़ोन" size={76} color="#EAF2FA" weight={900}
            style={{ fontFamily: head, textShadow: "0 8px 40px rgba(0,0,0,0.9)" }} />
        </div>
        <Scribble at={15.1} w={430} h={132} color={EMBER} strokeWidth={4} seed={7}
          style={{ left: "50%", top: "24%", transform: "translateX(-50%)" }} />
        <Label at={13.0} text="हम में से ज़्यादातर" size={22} color="rgba(226,236,246,0.6)"
          style={{ position: "absolute", left: 92, bottom: 96, fontFamily: lab }} />
      </Plate>

      {/* ── P4 15.9-19.0 · THE RESET ───────────────────────────────────────── */}
      <Plate from={15.9} to={19.0}>
        <Depth3D
          P={P}
          pad={1.16}
          layers={lay("A4_lamp_void")}
          cam={[
            [15.9, 0, 30, 40],
            [19.0, 0, -4, 0],
          ]}
        />
        <GodRays x="50%" y="86%" angle={186} opacity={0.16} color="255,196,120" />
        <div style={{ position: "absolute", left: 0, right: 0, top: "16%", textAlign: "center" }}>
          <Punch at={15.95} text="सीधे काम की बात" size={82} color="#F6EFE2" weight={900}
            style={{ fontFamily: head }} />
          <div style={{ height: 18 }} />
          <Punch at={17.67} text="गोत्र होता क्या है" size={58} color={EMBER} weight={700}
            style={{ fontFamily: head }} />
        </div>
      </Plate>

      {/* ── P5 19.0-22.7 · THREE WORDS, THREE DEPTHS ───────────────────────── */}
      <Plate from={19.0} to={22.7}>
        <Depth3D
          P={P}
          pad={1.16}
          layers={lay("A5_three_pillars")}
          cam={[
            [19.0, 40, 0, 0],
            [22.7, -46, -4, 88],
          ]}
        >
          <Fog z={1100} opacity={0.42} color="188,198,206" P={P} />
          <Fog z={520} opacity={0.2} color="188,198,206" P={P} />
          {/* each term is carved on its own pillar, at that pillar's depth */}
          <Carved at={19.2} text="गोत्र" z={120} x={26} y={44} />
          <Carved at={21.25} text="प्रवर" z={560} x={52} y={46} />
          <Carved at={22.02} text="सपिंड" z={980} x={74} y={47} />
        </Depth3D>
        <Vignette strength={0.55} color="10,14,18" />
        <Label at={19.9} text="तीन शब्द — एक ही सवाल के" size={22} color="rgba(232,238,242,0.62)"
          style={{ position: "absolute", left: 92, bottom: 88, fontFamily: lab }} />
      </Plate>

      {/* ── P6 22.7-29.9 · FOUR DOORWAYS ───────────────────────────────────── */}
      <Plate from={22.7} to={29.9}>
        <Depth3D
          P={P}
          pad={1.16}
          layers={lay("A6_corridor")}
          cam={[
            [22.7, 0, 0, 0],
            [29.9, 0, 0, 176],
          ]}
        >
          <Fog z={1200} opacity={0.3} color="214,190,150" P={P} />
        </Depth3D>
        <Vignette strength={0.6} color="18,12,6" />
        {DOORS.map(([t, w, x], i) => (
          <div key={w} style={{ position: "absolute", left: `${x}%`, top: `${62 + i * 1.5}%`, transform: "translateX(-50%)" }}>
            <Punch at={t} text={w} size={40 - i * 2} color={i === 3 ? EMBER : "#F2E9D8"} weight={700}
              style={{ fontFamily: head }} />
          </div>
        ))}
        <Highlight at={28.32} color="rgba(240,145,58,0.45)"
          style={{ position: "absolute", left: "70%", top: "63%", width: 190, height: 54, transform: "translateX(-50%)", zIndex: 0 }} />
      </Plate>

      {/* ── P7 29.9-37.1 · THE ROOT, THEN THE SPLIT ────────────────────────── */}
      <Plate from={29.9} to={37.1}>
        <PlateSeven />
      </Plate>

      {/* ── P8 37.1-45.6 · ĀRṢA / THE CLIMB ────────────────────────────────── */}
      <Plate from={37.1} to={45.6} tail={0}>
        <PlateEight />
      </Plate>

      <Grain opacity={0.15} />
    </AbsoluteFill>
  );
};

/* P7 gets its own component so its hooks sit above the Plate gate, not inside it. */
const PlateSeven: React.FC = () => {
  // the split: at 35.32 (दो) the frame divides; the two halves are the SAME plate graded apart
  const split = useKeys([[35.32, 0], [36.1, 1]], Easing.inOut(Easing.cubic));
  const step = useStep();
  return (
    <>
      <Depth3D
        P={P}
        pad={1.16}
        layers={lay("A7_banyan_roots")}
        cam={[
          [29.9, 0, -40, 0],
          [33.0, 0, 10, 92],
          [37.1, 18, 22, 150],
        ]}
      >
        <Fog z={1000} opacity={0.26} color="120,140,110" P={P} />
      </Depth3D>
      <Vignette strength={0.66} color="6,12,8" />

      {/* जड़ — the literal root word, on its own syllable */}
      <div style={{ position: "absolute", left: "50%", top: "22%", transform: "translateX(-50%)" }}>
        <Punch at={32.36} text="जड़" size={132} color="#F4EDE0" weight={900} out={34.6}
          style={{ fontFamily: head, textShadow: "0 10px 44px rgba(0,0,0,0.92)" }} />
      </div>

      {/* one word … two systems. The divider is the argument. */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          width: 2,
          background: `rgba(240,145,58,${(split * 0.85).toFixed(2)})`,
          transform: `translateX(-50%) scaleY(${split.toFixed(3)})`,
          transformOrigin: "50% 50%",
        }}
      />
      <div style={{ position: "absolute", left: 0, right: 0, top: "72%", textAlign: "center", opacity: split }}>
        <span style={{ ...devaBlock, fontFamily: lab, fontSize: 26, fontWeight: 600, color: "rgba(244,237,224,0.8)" }}>
          {step >= 0 ? "एक शब्द · दो व्यवस्थाएँ" : ""}
        </span>
      </div>
      <div style={{ position: "absolute", left: "25%", top: "50%", transform: "translate(-50%,-50%)" }}>
        <Punch at={35.58} text="पहली" size={54} color="#F2E9D8" weight={700} style={{ fontFamily: head }} />
      </div>
      <div style={{ position: "absolute", left: "75%", top: "50%", transform: "translate(-50%,-50%)" }}>
        <Punch at={35.84} text="दूसरी" size={54} color="rgba(242,233,216,0.42)" weight={700} style={{ fontFamily: head }} />
      </div>
    </>
  );
};

const PlateEight: React.FC = () => {
  const climb = useKeys(CLIMB, Easing.out(Easing.cubic));
  const headingIn = useSpringAt(39.79, { damping: 16, stiffness: 150 });
  return (
    <>
      <Depth3D
        P={P}
        pad={1.16}
        layers={lay("A8_ancestor_column")}
        cam={[
          [37.1, 0, 0, 0],
          [42.0, 0, -6, 74],
          [45.6, 0, -14, 168],
        ]}
      >
        <Fog z={1250} opacity={0.5} color="196,204,212" P={P} />
        <Fog z={640} opacity={0.24} color="196,204,212" P={P} />
      </Depth3D>
      <Vignette strength={0.62} color="10,14,20" />

      {/* Yash's BOXES→HEADING device: आर्ष rises into the page heading and STAYS for its section.
          That is what replaced four caption blocks. */}
      <div
        style={{
          position: "absolute",
          left: 92,
          top: 74,
          opacity: headingIn,
          transform: `translateY(${((1 - headingIn) * 26).toFixed(1)}px)`,
        }}
      >
        <span style={{ ...devaBlock, fontFamily: head, fontWeight: 900, fontSize: 66, color: "#F6F0E6" }}>
          आर्ष गोत्र
        </span>
        <div
          style={{
            height: 3,
            background: EMBER,
            transform: `scaleX(${headingIn.toFixed(3)})`,
            transformOrigin: "0% 50%",
            marginTop: 4,
          }}
        />
      </div>

      <div style={{ position: "absolute", left: 92, top: 186 }}>
        <Punch at={42.03} text="ऋषि से जुड़ा हुआ" size={34} color="rgba(238,244,250,0.82)" weight={700}
          style={{ fontFamily: lab }} />
      </div>

      {/* the climb: each spoken पिता lights one figure further back */}
      <div style={{ position: "absolute", right: 96, bottom: 104, textAlign: "right" }}>
        <Punch at={43.81} text="पिता" size={58} color="#F2E9D8" weight={900} style={{ fontFamily: head }} />
        <div style={{ height: 2 }} />
        <Punch at={45.33} text="उनके पिता" size={58} color={EMBER} weight={900} style={{ fontFamily: head }} />
      </div>
      <DrawLine
        at={43.81}
        d="M 1150 560 L 1150 300"
        len={260}
        color={`rgba(240,145,58,${(0.35 + climb * 0.5).toFixed(2)})`}
        strokeWidth={2}
        dur={1.5}
        dashed
      />
    </>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
